/**
 * useCytoscapeSelection Hook
 *
 * Manages node/edge selection, multi-select, and lasso selection
 */

import type { Core, EdgeSingular, NodeSingular } from 'cytoscape'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { SelectedEdge, SelectedNode } from './types'

export interface UseCytoscapeSelectionOptions {
  cy: Core | null
  onNodeSelect?: (node: SelectedNode | null) => void
  onEdgeSelect?: (edge: SelectedEdge | null) => void
  onMultiSelect?: (nodeIds: string[]) => void
}

export interface UseCytoscapeSelectionReturn {
  selectedNode: SelectedNode | null
  selectedEdge: SelectedEdge | null
  selectedNodeIds: Set<string>
  isMultiSelectMode: boolean
  // Actions
  selectNode: (nodeId: string) => void
  selectEdge: (edgeId: string) => void
  clearSelection: () => void
  toggleMultiSelectMode: () => void
  selectNodes: (nodeIds: string[]) => void
  addToSelection: (nodeIds: string[]) => void
  removeFromSelection: (nodeIds: string[]) => void
}

function cyNodeToSelectedNode(node: NodeSingular): SelectedNode {
  return {
    id: node.id(),
    name: node.data('displayName') || node.data('name') || node.id(),
    labels: node.data('labels') || [node.data('typeLabel')],
    typeLabel: node.data('typeLabel'),
    properties: node.data('properties') || {},
  }
}

function cyEdgeToSelectedEdge(edge: EdgeSingular): SelectedEdge {
  const source = edge.source()
  const target = edge.target()

  return {
    id: edge.id(),
    type: edge.data('type'),
    source: source.id(),
    target: target.id(),
    sourceName: source.data('displayName') || source.data('name'),
    targetName: target.data('displayName') || target.data('name'),
    properties: edge.data('properties') || {},
    sourceNode: {
      id: source.id(),
      labels: source.data('labels') || [source.data('typeLabel')],
      properties: source.data('properties') || {},
    },
    targetNode: {
      id: target.id(),
      labels: target.data('labels') || [target.data('typeLabel')],
      properties: target.data('properties') || {},
    },
  }
}

export function useCytoscapeSelection({
  cy,
  onNodeSelect,
  onEdgeSelect,
  onMultiSelect,
}: UseCytoscapeSelectionOptions): UseCytoscapeSelectionReturn {
  const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null)
  const [selectedEdge, setSelectedEdge] = useState<SelectedEdge | null>(null)
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set())
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false)

  const isListeningRef = useRef(false)

  // Set up event listeners
  useEffect(() => {
    if (!cy || isListeningRef.current) return
    isListeningRef.current = true

    // Node click
    const handleNodeClick = (evt: { target: NodeSingular }) => {
      const node = evt.target

      if (isMultiSelectMode) {
        // Multi-select mode: toggle in selection
        setSelectedNodeIds(prev => {
          const next = new Set(prev)
          if (next.has(node.id())) {
            next.delete(node.id())
            node.unselect()
          } else {
            next.add(node.id())
            node.select()
          }
          onMultiSelect?.(Array.from(next))
          return next
        })
      } else {
        // Single click: blue-highlight the node (via :selected styling) and
        // its directly connected edges. Leave everything else in its natural
        // state so timeline-driven dimming stays readable.
        cy.elements().unselect()
        node.select()

        cy.edges().removeClass('selection-highlight')
        node.connectedEdges().addClass('selection-highlight')

        const selected = cyNodeToSelectedNode(node)
        setSelectedNode(selected)
        setSelectedEdge(null)
        setSelectedNodeIds(new Set()) // Empty - no multi-selection
        onNodeSelect?.(selected)
      }
    }

    // Edge click
    const handleEdgeClick = (evt: { target: EdgeSingular }) => {
      const edge = evt.target
      // Clear node selection + any node-driven edge highlight; edge will
      // get its blue via the :selected selector.
      cy.nodes().unselect()
      cy.edges().removeClass('selection-highlight')
      edge.select()

      const selected = cyEdgeToSelectedEdge(edge)
      setSelectedEdge(selected)
      setSelectedNode(null)
      setSelectedNodeIds(new Set())
      onEdgeSelect?.(selected)
    }

    // Background click - only triggers when clicking on empty canvas
    const handleBackgroundClick = (evt: { target: Core }) => {
      // Only handle if clicking directly on the cy container, not on elements
      if (evt.target === cy) {
        cy.nodes().unselect()
        cy.edges().unselect()
        cy.edges().removeClass('selection-highlight')
        setSelectedNode(null)
        setSelectedEdge(null)
        setSelectedNodeIds(new Set())
        onNodeSelect?.(null)
        onEdgeSelect?.(null)
      }
    }

    // Box selection end (lasso)
    const handleBoxSelect = () => {
      const selected = cy.$(':selected')
      const nodeIds = selected.nodes().map((n: NodeSingular) => n.id())
      setSelectedNodeIds(new Set(nodeIds))
      onMultiSelect?.(nodeIds)
    }

    // The cytoscape-lasso plugin marks nodes as :selected directly. Mirror
    // that into multi-select state so the floating action bar shows after a
    // lasso pass. Coalesce the per-node select/unselect bursts into one
    // update on the next frame.
    let pendingSelectionSync: number | null = null
    const handleNodeSelectionChange = () => {
      if (pendingSelectionSync !== null) return
      pendingSelectionSync = requestAnimationFrame(() => {
        pendingSelectionSync = null
        const nodeIds = cy.$('node:selected').map((n: NodeSingular) => n.id())
        // Only surface as a multi-select when ≥2 nodes are selected; a single
        // selected node comes from a tap and is handled by onNodeSelect.
        if (nodeIds.length < 2) return
        setSelectedNodeIds(new Set(nodeIds))
        onMultiSelect?.(nodeIds)
      })
    }

    cy.on('tap', 'node', handleNodeClick)
    cy.on('tap', 'edge', handleEdgeClick)
    cy.on('tap', handleBackgroundClick)
    cy.on('boxend', handleBoxSelect)
    cy.on('select unselect', 'node', handleNodeSelectionChange)

    return () => {
      cy.off('tap', 'node', handleNodeClick)
      cy.off('tap', 'edge', handleEdgeClick)
      cy.off('tap', handleBackgroundClick)
      cy.off('boxend', handleBoxSelect)
      cy.off('select unselect', 'node', handleNodeSelectionChange)
      if (pendingSelectionSync !== null) cancelAnimationFrame(pendingSelectionSync)
      isListeningRef.current = false
    }
  }, [cy, isMultiSelectMode, onNodeSelect, onEdgeSelect, onMultiSelect])

  // Actions
  const selectNode = useCallback(
    (nodeId: string) => {
      if (!cy) return
      const node = cy.$id(nodeId)
      if (node.length > 0) {
        cy.elements().unselect()
        node.select()
        cy.edges().removeClass('selection-highlight')
        node.connectedEdges().addClass('selection-highlight')
        const selected = cyNodeToSelectedNode(node)
        setSelectedNode(selected)
        setSelectedEdge(null)
        onNodeSelect?.(selected)
      }
    },
    [cy, onNodeSelect]
  )

  const selectEdge = useCallback(
    (edgeId: string) => {
      if (!cy) return
      const edge = cy.$id(edgeId)
      if (edge.length > 0 && edge.isEdge()) {
        cy.edges().unselect()
        edge.select()
        const selected = cyEdgeToSelectedEdge(edge as EdgeSingular)
        setSelectedEdge(selected)
        setSelectedNode(null)
        onEdgeSelect?.(selected)
      }
    },
    [cy, onEdgeSelect]
  )

  const clearSelection = useCallback(() => {
    if (!cy) return
    cy.elements().unselect()
    cy.edges().removeClass('selection-highlight')
    // Also clear legacy blanket focus-dim state in case the multi-node
    // highlightNodes() ref API was used previously.
    cy.elements().removeClass('focus-dimmed')
    setSelectedNode(null)
    setSelectedEdge(null)
    setSelectedNodeIds(new Set())
    onNodeSelect?.(null)
    onEdgeSelect?.(null)
    onMultiSelect?.([])
  }, [cy, onNodeSelect, onEdgeSelect, onMultiSelect])

  const toggleMultiSelectMode = useCallback(() => {
    setIsMultiSelectMode(prev => !prev)
    if (isMultiSelectMode) {
      // Exiting multi-select mode - clear selection
      clearSelection()
    }
  }, [isMultiSelectMode, clearSelection])

  const selectNodes = useCallback(
    (nodeIds: string[]) => {
      if (!cy) return
      cy.nodes().unselect()
      nodeIds.forEach(id => {
        cy.$id(id).select()
      })
      setSelectedNodeIds(new Set(nodeIds))
      onMultiSelect?.(nodeIds)
    },
    [cy, onMultiSelect]
  )

  const addToSelection = useCallback(
    (nodeIds: string[]) => {
      if (!cy) return
      setSelectedNodeIds(prev => {
        const next = new Set(prev)
        nodeIds.forEach(id => {
          next.add(id)
          cy.$id(id).select()
        })
        onMultiSelect?.(Array.from(next))
        return next
      })
    },
    [cy, onMultiSelect]
  )

  const removeFromSelection = useCallback(
    (nodeIds: string[]) => {
      if (!cy) return
      setSelectedNodeIds(prev => {
        const next = new Set(prev)
        nodeIds.forEach(id => {
          next.delete(id)
          cy.$id(id).unselect()
        })
        onMultiSelect?.(Array.from(next))
        return next
      })
    },
    [cy, onMultiSelect]
  )

  return {
    selectedNode,
    selectedEdge,
    selectedNodeIds,
    isMultiSelectMode,
    selectNode,
    selectEdge,
    clearSelection,
    toggleMultiSelectMode,
    selectNodes,
    addToSelection,
    removeFromSelection,
  }
}
